import { useMemo } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { AppPageHeader } from "@themes/pollinator/ui/AppPageHeader";
import { Loader } from "@themes/pollinator/ui/Loader";
import {
  useFetchActions,
  hasAction,
  ACTION_TYPE_SLUG,
} from "@core/features/actions";
import { usePlatformStore } from "@platform/store";
import {
  ProgramTabs,
  ScheduleTabContent,
  CircleTabContent,
  type TabValue,
} from "@platform/features/program";

const VALID_TABS: TabValue[] = ["schedule", "cohort"];

/**
 * Program page with tabbed navigation for Schedule and Cohort.
 *
 * Routes:
 * - /program/:id → redirects to default tab (schedule if available, otherwise cohort)
 * - /program/:id/schedule → Schedule tab (requires SCHEDULE action)
 * - /program/:id/cohort → Cohort tab (requires CIRCLE_VIEW action)
 *
 * Authorization:
 * - Tabs are conditionally rendered based on user's actions
 * - If user has no valid actions for either tab, redirects to dashboard
 */
export function ProgramPage() {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const user = usePlatformStore((state) => state.user);
  const programId = Number(id);
  const isValidId = !!id && !isNaN(programId);

  // Fetch user actions to determine which tabs to show
  const { data: actionsData, isLoading } = useFetchActions({
    programId,
    user,
    isEnabled: !!user && isValidId,
  });

  // Determine which tabs to show based on user's actions
  const showSchedule = useMemo(() => {
    if (!actionsData?.actions) return false;
    return hasAction(actionsData.actions, ACTION_TYPE_SLUG.SCHEDULE);
  }, [actionsData?.actions]);

  // Cohort tab is always shown, but content depends on whether user has access
  const hasCircleAccess = useMemo(() => {
    if (!actionsData?.actions) return false;
    return hasAction(actionsData.actions, ACTION_TYPE_SLUG.CIRCLE_VIEW);
  }, [actionsData?.actions]);

  // Always show Cohort tab
  const showCohort = true;

  // Determine default tab
  const defaultTab = useMemo((): TabValue | null => {
    if (showSchedule) return "schedule";
    if (showCohort) return "cohort";
    return null;
  }, [showSchedule, showCohort]);

  // Redirect to dashboard if programId is invalid
  if (!isValidId) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show loader while checking actions
  if (isLoading) {
    return <Loader isFullPage />;
  }

  // Redirect to dashboard if user has no valid tabs
  if (!showSchedule && !showCohort) {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect to default tab if no tab specified
  if (!tab && defaultTab) {
    return <Navigate to={`/program/${id}/${defaultTab}`} replace />;
  }

  // Validate and set active tab
  const activeTab: TabValue = VALID_TABS.includes(tab as TabValue)
    ? (tab as TabValue)
    : defaultTab || "schedule";

  // Redirect to valid tab if invalid tab in URL
  if (tab && !VALID_TABS.includes(tab as TabValue) && defaultTab) {
    return <Navigate to={`/program/${id}/${defaultTab}`} replace />;
  }

  // Redirect if user tries to access a tab they don't have permission for
  if (
    (activeTab === "schedule" && !showSchedule) ||
    (activeTab === "cohort" && !showCohort)
  ) {
    if (defaultTab) {
      return <Navigate to={`/program/${id}/${defaultTab}`} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const handleTabChange = (newTab: TabValue) => {
    navigate(`/program/${id}/${newTab}`);
  };

  return (
    <>
      <AppPageHeader
        title="Program"
        description="Manage your program schedule and peer cohort."
        tabs={
          <ProgramTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            showSchedule={showSchedule}
            showCohort={showCohort}
          />
        }
      />
      {activeTab === "schedule" && showSchedule && (
        <ScheduleTabContent programId={programId} />
      )}
      {activeTab === "cohort" && showCohort && (
        <CircleTabContent programId={programId} hasAccess={hasCircleAccess} />
      )}
    </>
  );
}

export default ProgramPage;
